// Index-based, gap-collapsed viewport model + coordinate transforms.
//
// Horizontal: bars are evenly spaced by `barSpacing` px regardless of real-time
// gaps. `firstIndex` is the (fractional) bar index at the left edge of the plot.
// Vertical: price scale maps [priceMin, priceMax] to the plot height.

export interface PlotRect {
  left: number;
  top: number;
  width: number;
  height: number;
}

export const MIN_BAR_SPACING = 0.05;
export const MAX_BAR_SPACING = 120;

export class Viewport {
  // horizontal
  firstIndex = 0; // fractional bar index at left edge
  barSpacing = 8; // px per bar
  rightOffset = 12; // bars of empty space kept to the right of the last bar

  // vertical price scale (data units)
  priceMin = 0;
  priceMax = 1;
  priceAuto = true;

  // plot area in CSS pixels (excludes axes)
  plot: PlotRect = { left: 0, top: 0, width: 0, height: 0 };

  // total bars available in the active timeframe
  barCount = 0;

  setPlot(left: number, top: number, width: number, height: number) {
    this.plot.left = left;
    this.plot.top = top;
    this.plot.width = Math.max(0, width);
    this.plot.height = Math.max(0, height);
  }

  clampBarSpacing() {
    if (this.barSpacing < MIN_BAR_SPACING) this.barSpacing = MIN_BAR_SPACING;
    if (this.barSpacing > MAX_BAR_SPACING) this.barSpacing = MAX_BAR_SPACING;
  }

  get barsVisible(): number {
    return this.plot.width / this.barSpacing;
  }

  get lastIndex(): number {
    return this.firstIndex + this.barsVisible;
  }

  get centerIndex(): number {
    return this.firstIndex + this.barsVisible / 2;
  }

  // --- horizontal transforms (relative to plot area) ---
  // x is in CSS px relative to the plot's left edge (0 = plot.left).
  xOfIndex(i: number): number {
    return (i - this.firstIndex) * this.barSpacing;
  }
  // center of a bar's candle
  xCenterOfIndex(i: number): number {
    return (i - this.firstIndex) * this.barSpacing + this.barSpacing / 2;
  }
  indexOfX(x: number): number {
    return this.firstIndex + x / this.barSpacing;
  }
  // nearest integer bar index for a plot-relative x
  barIndexAtX(x: number): number {
    return Math.floor(this.firstIndex + x / this.barSpacing);
  }

  // --- vertical transforms ---
  yOfPrice(p: number): number {
    const { top, height } = this.plot;
    const span = this.priceMax - this.priceMin || 1;
    return top + (this.priceMax - p) / span * height;
  }
  priceOfY(y: number): number {
    const { top, height } = this.plot;
    const span = this.priceMax - this.priceMin || 1;
    return this.priceMax - ((y - top) / height) * span;
  }

  // Clamp horizontal scroll so at least a few bars remain on screen.
  clampScroll() {
    const maxFirst = this.barCount - 1 + this.rightOffset - Math.min(this.barsVisible, this.barCount) * 0.15;
    const minFirst = -this.rightOffset - this.barsVisible * 0.5;
    if (this.firstIndex > maxFirst) this.firstIndex = maxFirst;
    if (this.firstIndex < minFirst) this.firstIndex = minFirst;
  }

  // Visible integer index range [lo, hi) clamped to available bars.
  visibleRange(): [number, number] {
    const lo = Math.max(0, Math.floor(this.firstIndex) - 1);
    const hi = Math.min(this.barCount, Math.ceil(this.lastIndex) + 1);
    return [lo, Math.max(lo, hi)];
  }

  scrollToRight() {
    this.firstIndex = this.barCount - this.barsVisible + this.rightOffset;
  }
}
